import React from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
    TCarCalendar,
    TBookingSlot,
    TWorkOrderSlot,
} from '../start-work-order/type';

type Props = {
    month: dayjs.Dayjs;
    carData: TCarCalendar[];
    mobileMode: boolean;
};

const COLORS = [
    '#7357FF',
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E9',
    '#F8C471',
    '#82E0AA',
    '#F1948A',
    '#AED6F1',
    '#D7BDE2',
];

const WORK_ORDER_COLOR = '#f59e0b'; // amber

function getCarColor(index: number): string {
    return COLORS[index % COLORS.length];
}

function isBooked(
    date: dayjs.Dayjs,
    bookings: TBookingSlot[]
): TBookingSlot | null {
    for (const b of bookings) {
        const start = dayjs(b.tripStart);
        const end = dayjs(b.tripEnd);
        if (!date.isBefore(start, 'day') && !date.isAfter(end, 'day')) {
            return b;
        }
    }
    return null;
}

function getWorkOrder(
    date: dayjs.Dayjs,
    workOrders: TWorkOrderSlot[]
): TWorkOrderSlot | null {
    for (const wo of workOrders) {
        if (!wo.startDate) continue;
        const start = dayjs(wo.startDate).startOf('day');
        // If no end date, mark just the start day (single-day service created but not started)
        const end = wo.endDate ? dayjs(wo.endDate).startOf('day') : start;
        if (!date.isBefore(start, 'day') && !date.isAfter(end, 'day')) {
            return wo;
        }
    }
    return null;
}

export const CalendarGrid: React.FC<Props> = ({
    month,
    carData,
    mobileMode,
}) => {
    const navigate = useNavigate();
    const daysInMonth = month.daysInMonth();

    const statusLabels: Record<string, string> = {
        PENDING: 'Pending',
        INPROGRESS: 'In Progress',
        ON_HOLD: 'On Hold',
        DONE: 'Done',
    };

    const priorityLabels: Record<string, string> = {
        URGENT: 'Urgent',
        HIGH: 'High',
        MEDIUM: 'Medium',
        LOW: 'Low',
    };

    return (
        <div className="overflow-x-auto">
            <table
                className="w-full border-collapse"
                style={{ minWidth: mobileMode ? 'auto' : '768px' }}
            >
                <thead>
                    <tr>
                        <th className="sticky left-0 bg-gray-50 z-10 border-b border-r p-2 text-left min-w-[160px] font-semibold text-sm">
                            Vehicle
                        </th>
                        {Array.from({ length: daysInMonth }, (_, i) => (
                            <th
                                key={i}
                                className="border-b border-r p-1 text-center text-xs font-medium text-gray-500 min-w-[36px] bg-gray-50"
                            >
                                {i + 1}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {carData.map((car, carIndex) => {
                        const color = getCarColor(carIndex);
                        return (
                            <tr key={car.vin || carIndex}>
                                <td className="sticky left-0 bg-white z-10 border-b border-r p-2 text-sm font-medium truncate max-w-[160px]">
                                    {car.vehicleName}
                                </td>
                                {Array.from(
                                    { length: daysInMonth },
                                    (_, dayIdx) => {
                                        const date = month.date(dayIdx + 1);
                                        const booking = isBooked(
                                            date,
                                            car.bookings
                                        );
                                        const wo = getWorkOrder(
                                            date,
                                            car.workOrders
                                        );

                                        // BOOKED: reservation — NOT clickable
                                        if (booking) {
                                            return (
                                                <td
                                                    key={dayIdx}
                                                    className="border-b border-r p-0"
                                                    style={{ height: '36px' }}
                                                >
                                                    <Tooltip
                                                        title={
                                                            <div>
                                                                <div>
                                                                    <strong>
                                                                        🔒
                                                                        BOOKED
                                                                    </strong>
                                                                </div>
                                                                <div>
                                                                    <strong>
                                                                        Guest:
                                                                    </strong>{' '}
                                                                    {booking.guest ||
                                                                        'N/A'}
                                                                </div>
                                                                <div>
                                                                    <strong>
                                                                        Reservation:
                                                                    </strong>{' '}
                                                                    {
                                                                        booking.reservationId
                                                                    }
                                                                </div>
                                                                <div>
                                                                    <strong>
                                                                        Status:
                                                                    </strong>{' '}
                                                                    {
                                                                        booking.tripStatus
                                                                    }
                                                                </div>
                                                                <div>
                                                                    <strong>
                                                                        Dates:
                                                                    </strong>{' '}
                                                                    {dayjs(
                                                                        booking.tripStart
                                                                    ).format(
                                                                        'MMM D'
                                                                    )}{' '}
                                                                    –{' '}
                                                                    {dayjs(
                                                                        booking.tripEnd
                                                                    ).format(
                                                                        'MMM D'
                                                                    )}
                                                                </div>
                                                                <div className="text-red-300 mt-1">
                                                                    Cannot
                                                                    create work
                                                                    order on
                                                                    booked days
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <div
                                                            className="w-full h-full"
                                                            style={{
                                                                backgroundColor:
                                                                    color,
                                                                opacity: 0.6,
                                                                cursor: 'not-allowed',
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </td>
                                            );
                                        }

                                        // WORK ORDER: active service — clickable, shows service names
                                        if (wo) {
                                            return (
                                                <td
                                                    key={dayIdx}
                                                    className="border-b border-r p-0 group"
                                                    style={{ height: '36px' }}
                                                >
                                                    <Tooltip
                                                        title={
                                                            <div>
                                                                <div>
                                                                    <strong>
                                                                        🔧 Work
                                                                        Order #
                                                                        {
                                                                            wo.carInServiceId
                                                                        }
                                                                    </strong>
                                                                </div>
                                                                <div>
                                                                    <strong>
                                                                        Status:
                                                                    </strong>{' '}
                                                                    {statusLabels[
                                                                        wo.status ||
                                                                            ''
                                                                    ] ||
                                                                        wo.status ||
                                                                        'N/A'}
                                                                </div>
                                                                <div>
                                                                    <strong>
                                                                        Priority:
                                                                    </strong>{' '}
                                                                    {priorityLabels[
                                                                        wo.priority ||
                                                                            ''
                                                                    ] ||
                                                                        wo.priority ||
                                                                        'N/A'}
                                                                </div>
                                                                {wo.serviceNames &&
                                                                    wo
                                                                        .serviceNames
                                                                        .length >
                                                                        0 && (
                                                                        <div className="mt-1">
                                                                            <strong>
                                                                                Services:
                                                                            </strong>
                                                                            <ul className="list-disc pl-4 mt-0.5">
                                                                                {wo.serviceNames.map(
                                                                                    (
                                                                                        name,
                                                                                        i
                                                                                    ) => (
                                                                                        <li
                                                                                            key={
                                                                                                i
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                name
                                                                                            }
                                                                                        </li>
                                                                                    )
                                                                                )}
                                                                            </ul>
                                                                        </div>
                                                                    )}
                                                                {wo.startDate && (
                                                                    <div className="mt-1">
                                                                        <strong>
                                                                            Since:
                                                                        </strong>{' '}
                                                                        {dayjs(
                                                                            wo.startDate
                                                                        ).format(
                                                                            'MMM D, YYYY'
                                                                        )}
                                                                    </div>
                                                                )}
                                                                <div className="text-amber-300 mt-1">
                                                                    Click to
                                                                    view /
                                                                    modify
                                                                    services
                                                                </div>
                                                            </div>
                                                        }
                                                    >
                                                        <div
                                                            className="w-full h-full cursor-pointer flex items-center justify-center"
                                                            style={{
                                                                backgroundColor:
                                                                    WORK_ORDER_COLOR,
                                                                opacity: 0.6,
                                                            }}
                                                            onClick={() => {
                                                                // Navigate to All Work Orders — the page will show all orders
                                                                navigate(
                                                                    '/all-work-orders'
                                                                );
                                                            }}
                                                        >
                                                            <span className="text-[10px] text-white font-bold opacity-70">
                                                                WO#
                                                                {
                                                                    wo.carInServiceId
                                                                }
                                                            </span>
                                                        </div>
                                                    </Tooltip>
                                                </td>
                                            );
                                        }

                                        // AVAILABLE: empty — clickable
                                        const isWeekend =
                                            date.day() === 0 ||
                                            date.day() === 6;
                                        return (
                                            <td
                                                key={dayIdx}
                                                className="border-b border-r p-0 group"
                                                style={{ height: '36px' }}
                                            >
                                                <Tooltip
                                                    title={`Create work order for ${car.vehicleName} on ${date.format('MMM D, YYYY')}`}
                                                >
                                                    <div
                                                        className="w-full h-full flex items-center justify-center cursor-pointer transition-colors"
                                                        style={{
                                                            backgroundColor:
                                                                isWeekend
                                                                    ? '#f9fafb'
                                                                    : 'transparent',
                                                        }}
                                                        onMouseEnter={e => {
                                                            e.currentTarget.style.backgroundColor =
                                                                '#e8f4fd';
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.currentTarget.style.backgroundColor =
                                                                isWeekend
                                                                    ? '#f9fafb'
                                                                    : 'transparent';
                                                        }}
                                                        onClick={() => {
                                                            const dateStr =
                                                                date.format(
                                                                    'YYYY-MM-DD'
                                                                );
                                                            const carId =
                                                                car.carId;
                                                            if (carId) {
                                                                navigate(
                                                                    `/start-work-order?carId=${carId}&date=${dateStr}`
                                                                );
                                                            } else {
                                                                navigate(
                                                                    `/start-work-order?vin=${encodeURIComponent(car.vin)}&date=${dateStr}`
                                                                );
                                                            }
                                                        }}
                                                    >
                                                        <PlusOutlined
                                                            className="opacity-0 group-hover:opacity-40 text-xs"
                                                            style={{
                                                                color: '#7357FF',
                                                            }}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </td>
                                        );
                                    }
                                )}
                            </tr>
                        );
                    })}
                    {carData.length === 0 && (
                        <tr>
                            <td
                                colSpan={daysInMonth + 1}
                                className="text-center py-8 text-gray-400 border-b"
                            >
                                No vehicles found
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            <div className="flex items-center gap-6 mt-4 text-xs text-gray-500 px-2">
                <div className="flex items-center gap-1">
                    <div
                        className="w-3 h-3 rounded-sm opacity-60"
                        style={{ backgroundColor: COLORS[0] }}
                    />
                    <span>Booked (Turo)</span>
                </div>
                <div className="flex items-center gap-1">
                    <div
                        className="w-3 h-3 rounded-sm opacity-70"
                        style={{ backgroundColor: WORK_ORDER_COLOR }}
                    />
                    <span>Work Order</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm border border-gray-300 bg-white" />
                    <span>Available</span>
                </div>
            </div>
        </div>
    );
};
